import z from "zod/v3";

export const MAX_NAME_LENGTH = 80;
export const MAX_PROJECT_ACRONYM_LENGTH = 3;
export const MAX_PROJECT_DESCRIPTION_LENGTH = 350;

export const createProjectSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, "Project name is required")
      .max(
        MAX_NAME_LENGTH,
        `Project name cannot exceed ${MAX_NAME_LENGTH} characters`,
      ),

    acronym: z.preprocess(
      (value) => {
        if (typeof value !== "string") {
          return value;
        }

        const normalizedValue = value.trim().toUpperCase();

        return normalizedValue || undefined;
      },
      z
        .string()
        .max(
          MAX_PROJECT_ACRONYM_LENGTH,
          `Project acronym cannot exceed ${MAX_PROJECT_ACRONYM_LENGTH} characters`,
        )
        .optional(),
    ),

    area: z.preprocess(
      (value) => {
        if (
          value === "" ||
          value === null ||
          value === undefined ||
          (typeof value === "number" && Number.isNaN(value))
        ) {
          return undefined;
        }

        return value;
      },
      z.coerce
        .number({
          invalid_type_error: "Project area must be a number",
        })
        .positive("Project area must be greater than zero")
        .optional(),
    ),

    description: z
      .string()
      .trim()
      .max(
        MAX_PROJECT_DESCRIPTION_LENGTH,
        `Project description cannot exceed ${MAX_PROJECT_DESCRIPTION_LENGTH} characters`,
      ),

    imageId: z.string().uuid().optional(),
    templateId: z.string().optional(),
    ownerType: z.enum(["personal", "organization"]),
    organizationId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.ownerType === "organization" && !data.organizationId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["organizationId"],
        message: "Organization is required",
      });
    }
  });

export type CreateProjectFormInput = z.input<typeof createProjectSchema>;
export type CreateProjectFormData = z.output<typeof createProjectSchema>;
